/**
 * GeoJSON normalisation: converts raw Overpass/ORM responses to a unified
 * NormalisedFeatureCollection with provenance metadata on every Feature.
 */

import type { OsmJson, OsmElement } from '../../adapters/overpass/index.js';
import type { OrmFacilityResult, OrmMilestoneResult } from '../../adapters/orm-api/index.js';
import type { ExecutionPlan, PostFilter } from '../../planner/index.js';
import type { MappingStatus } from '../../ast/registry-types.js';

// ---------------------------------------------------------------------------
// Normalised GeoJSON types
// ---------------------------------------------------------------------------

export interface ProvenanceProperties {
  /** 'osm' | 'orm-facility' | 'orm-milestone' */
  source_system: string;
  /** 'overpass' | 'orm-facility-api' | 'orm-milestone-api' */
  source_endpoint_kind: string;
  mapping_status: MappingStatus;
  mapping_confidence: number;
  translation_notes: string[];
  /** What field/algorithm was used to match this feature */
  matched_by: string;
  /** Raw OSM/ORM tags */
  original_tags: Record<string, string>;
  /** ERA entity class that was queried */
  era_entity: string;
  /** OSM element IDs included in this feature */
  osm_ids: number[];
}

export type NormalisedProperties = ProvenanceProperties & Record<string, unknown>;

export interface NormalisedFeature {
  type: 'Feature';
  geometry:
    | { type: 'Point'; coordinates: [number, number] }
    | { type: 'LineString'; coordinates: Array<[number, number]> }
    | { type: 'MultiLineString'; coordinates: Array<Array<[number, number]>> }
    | { type: 'Polygon'; coordinates: Array<Array<[number, number]>> }
    | null;
  properties: NormalisedProperties;
}

export interface NormalisedFeatureCollection {
  type: 'FeatureCollection';
  features: NormalisedFeature[];
  /** Top-level metadata */
  metadata: {
    source_system: string;
    queried_era_entity: string;
    partial_translation: boolean;
    unmapped_fields: string[];
    warnings: string[];
    generated_at: string;
  };
}

// ---------------------------------------------------------------------------
// Main normalisation entry point
// ---------------------------------------------------------------------------

type RawResult = OsmJson | OrmFacilityResult | OrmMilestoneResult;

export function toFeatureCollection(
  raw: RawResult,
  executionPlan: ExecutionPlan,
): NormalisedFeatureCollection {
  const metadata = buildMetadata(executionPlan);

  let features: NormalisedFeature[] = [];

  if (isOsmJson(raw)) {
    features = convertOsmJson(raw, executionPlan);
  } else if (isOrmFacilityResult(raw)) {
    features = convertOrmFacility(raw, executionPlan);
  } else if (isOrmMilestoneResult(raw)) {
    features = convertOrmMilestone(raw, executionPlan);
  }

  // Apply post-filters if present
  const postFilters = getPostFilters(executionPlan);
  if (postFilters.length > 0) {
    features = applyPostFilters(features, postFilters);
  }

  return { type: 'FeatureCollection', features, metadata };
}

// ---------------------------------------------------------------------------
// OSM JSON → GeoJSON Features
// ---------------------------------------------------------------------------

function convertOsmJson(
  osmJson: OsmJson,
  plan: ExecutionPlan,
): NormalisedFeature[] {
  const rule = getRule(plan);
  const eraEntity = rule?.eraEntity ?? 'unknown';
  const status = rule?.status ?? 'approximate';
  const confidence = rule?.confidence ?? 0.5;

  return osmJson.elements
    .map((el): NormalisedFeature | null => {
      const geometry = osmElementToGeometry(el);
      if (!geometry && el.type !== 'node') return null;

      const props: NormalisedProperties = {
        source_system: 'osm',
        source_endpoint_kind: 'overpass',
        mapping_status: status,
        mapping_confidence: confidence,
        translation_notes: [],
        matched_by: 'overpass-tag-filter',
        original_tags: el.tags ?? {},
        era_entity: eraEntity,
        osm_ids: [el.id],
        // Surface useful OSM properties at top level for Leaflet popups
        name: el.tags?.name ?? '',
        railway: el.tags?.railway ?? '',
        uic_ref: el.tags?.uic_ref ?? '',
        ref: el.tags?.ref ?? el.tags?.['railway:ref'] ?? '',
        maxspeed: el.tags?.maxspeed ?? '',
        electrified: el.tags?.electrified ?? '',
        voltage: el.tags?.voltage ?? '',
        frequency: el.tags?.frequency ?? '',
        gauge: el.tags?.gauge ?? '',
        operator: el.tags?.operator ?? '',
      };

      return { type: 'Feature', geometry, properties: props };
    })
    .filter((f): f is NormalisedFeature => f !== null);
}

function osmElementToGeometry(el: OsmElement): NormalisedFeature['geometry'] {
  if (el.type === 'node') {
    const lat = el.lat ?? el.center?.lat;
    const lon = el.lon ?? el.center?.lon;
    if (lat === undefined || lon === undefined) return null;
    return { type: 'Point', coordinates: [lon, lat] };
  }

  if (el.type === 'way') {
    // Center point (for OP ways)
    if (el.center) {
      if (el.geometry && el.geometry.length >= 2) {
        return {
          type: 'LineString',
          coordinates: el.geometry.map((p) => [p.lon, p.lat]),
        };
      }
      return { type: 'Point', coordinates: [el.center.lon, el.center.lat] };
    }
    if (el.geometry && el.geometry.length >= 2) {
      return {
        type: 'LineString',
        coordinates: el.geometry.map((p) => [p.lon, p.lat]),
      };
    }
    return null;
  }

  // Relations: use center if available
  if (el.type === 'relation' && el.center) {
    return { type: 'Point', coordinates: [el.center.lon, el.center.lat] };
  }

  return null;
}

// ---------------------------------------------------------------------------
// ORM Facility → GeoJSON Features
// ---------------------------------------------------------------------------

function convertOrmFacility(
  result: OrmFacilityResult,
  plan: ExecutionPlan,
): NormalisedFeature[] {
  const rule = getRule(plan);
  const eraEntity = rule?.eraEntity ?? 'era:OperationalPoint';
  const confidence = rule?.confidence ?? 0.7;
  const status = rule?.status ?? 'approximate';

  return result.facilities.map((f): NormalisedFeature => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [f.lon, f.lat] },
    properties: {
      source_system: 'orm-facility',
      source_endpoint_kind: 'orm-facility-api',
      mapping_status: status,
      mapping_confidence: confidence,
      translation_notes: [],
      matched_by: plan.kind === 'orm-facility-lookup' ? `orm-${plan.searchType}` : 'orm-facility',
      original_tags: f.tags ?? {},
      era_entity: eraEntity,
      osm_ids: [f.id],
      name: f.name,
      railway: f.railway ?? '',
      uic_ref: f.uic_ref ?? '',
    },
  }));
}

// ---------------------------------------------------------------------------
// ORM Milestone → GeoJSON Features
// ---------------------------------------------------------------------------

function convertOrmMilestone(
  result: OrmMilestoneResult,
  plan: ExecutionPlan,
): NormalisedFeature[] {
  const rule = getRule(plan);
  const eraEntity = rule?.eraEntity ?? 'era:Track';
  const confidence = rule?.confidence ?? 0.5;
  const status = rule?.status ?? 'approximate';

  return result.milestones.map((m): NormalisedFeature => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [m.lon, m.lat] },
    properties: {
      source_system: 'orm-milestone',
      source_endpoint_kind: 'orm-milestone-api',
      mapping_status: status,
      mapping_confidence: confidence,
      translation_notes: [],
      matched_by: 'orm-milestone-km',
      original_tags: m.tags ?? {},
      era_entity: eraEntity,
      osm_ids: [m.id],
      line: m.line,
      km: m.km,
    },
  }));
}

// ---------------------------------------------------------------------------
// Post-filters
// ---------------------------------------------------------------------------

function applyPostFilters(
  features: NormalisedFeature[],
  filters: PostFilter[],
): NormalisedFeature[] {
  return features.filter((feature) => {
    const tags = feature.properties.original_tags;
    for (const pf of filters) {
      if (pf.excludeTagValues) {
        for (const [key, disallowed] of Object.entries(pf.excludeTagValues)) {
          if (key in tags && disallowed.includes(tags[key]!)) return false;
        }
      }
      if (pf.requireTagValues) {
        for (const [key, required] of Object.entries(pf.requireTagValues)) {
          if (!(key in tags) || !required.includes(tags[key]!)) return false;
        }
      }
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// Metadata helpers
// ---------------------------------------------------------------------------

function buildMetadata(plan: ExecutionPlan): NormalisedFeatureCollection['metadata'] {
  const partial = plan.kind === 'partial-translation';
  const unmapped = partial && plan.kind === 'partial-translation' ? plan.unmappedFields : [];
  const warnings = partial && plan.kind === 'partial-translation' ? plan.warnings : [];
  const rule = getRule(plan);

  return {
    source_system: plan.kind.startsWith('orm') ? 'orm' : 'osm',
    queried_era_entity: rule?.eraEntity ?? 'unknown',
    partial_translation: partial,
    unmapped_fields: unmapped,
    warnings,
    generated_at: new Date().toISOString(),
  };
}

function getPostFilters(plan: ExecutionPlan): PostFilter[] {
  if (plan.kind === 'overpass-primary') return plan.postFilters;
  if (plan.kind === 'overpass-plus-postfilter') return plan.postFilters;
  if (plan.kind === 'partial-translation' && plan.basePlan) {
    if (plan.basePlan.kind === 'overpass-primary') return plan.basePlan.postFilters;
  }
  return [];
}

function getRule(plan: ExecutionPlan) {
  if (plan.kind === 'orm-facility-lookup') return plan.mappingRule;
  if (plan.kind === 'orm-milestone-lookup') return plan.mappingRule;
  if (plan.kind === 'overpass-primary') return plan.mappingRule;
  if (plan.kind === 'overpass-plus-postfilter') return plan.mappingRule;
  if (plan.kind === 'partial-translation' && plan.basePlan) return getRule(plan.basePlan);
  return null;
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

function isOsmJson(r: RawResult): r is OsmJson {
  return 'elements' in r && Array.isArray((r as OsmJson).elements);
}

function isOrmFacilityResult(r: RawResult): r is OrmFacilityResult {
  return 'facilities' in r;
}

function isOrmMilestoneResult(r: RawResult): r is OrmMilestoneResult {
  return 'milestones' in r;
}
